import {
  DEFAULT_APP_LOCALE,
  type SupportedLocale,
  SUPPORTED_LOCALES,
  resolveSupportedLocale,
} from "@/lib/i18n/config";

export const REQUEST_LOCALE_HEADER_NAME = "x-landfast-request-locale";

const PUBLIC_LOCALE_PATH_PREFIXES = [
  "/",
  "/blog",
  "/pricing",
  "/privacy",
  "/terms",
  "/resume-examples",
  "/tools",
  "/resume-builder",
  "/job-finder",
  "/cover-letter-generator",
  "/interview-prep",
  "/resume-checker",
] as const;

const LOCALE_SEGMENT_SET = new Set<string>(SUPPORTED_LOCALES);

const ensureLeadingSlash = (pathname: string) =>
  pathname.startsWith("/") ? pathname : `/${pathname}`;

const normalizePathname = (pathname: string) => {
  const withLeadingSlash = ensureLeadingSlash(pathname || "/");
  if (withLeadingSlash === "/") {
    return withLeadingSlash;
  }

  return withLeadingSlash.replace(/\/+$/, "") || "/";
};

const splitHref = (href: string) => {
  const hashIndex = href.indexOf("#");
  const hash = hashIndex >= 0 ? href.slice(hashIndex) : "";
  const withoutHash = hashIndex >= 0 ? href.slice(0, hashIndex) : href;
  const queryIndex = withoutHash.indexOf("?");
  const query = queryIndex >= 0 ? withoutHash.slice(queryIndex) : "";
  const pathname = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;

  return {
    pathname: pathname || "/",
    query,
    hash,
  };
};

export const isPublicLocalePathname = (pathname: string): boolean => {
  const normalized = normalizePathname(pathname);

  return PUBLIC_LOCALE_PATH_PREFIXES.some((prefix) => {
    if (prefix === "/") {
      return normalized === "/";
    }

    return normalized === prefix || normalized.startsWith(`${prefix}/`);
  });
};

export const getLocaleFromHref = (href: string): SupportedLocale | null => {
  const { pathname } = splitHref(href);
  const [firstSegment] = normalizePathname(pathname).split("/").filter(Boolean);

  if (!firstSegment) {
    return null;
  }

  return LOCALE_SEGMENT_SET.has(firstSegment)
    ? (firstSegment as SupportedLocale)
    : null;
};

export const getPublicLocaleFromPathname = (
  pathname: string
): SupportedLocale | null => {
  const locale = getLocaleFromHref(pathname);
  if (locale) {
    return locale;
  }

  return isPublicLocalePathname(pathname) ? DEFAULT_APP_LOCALE : null;
};

export const localizePublicHref = (
  locale: SupportedLocale | null | undefined,
  href: string
): string => {
  if (!locale || href.startsWith("http://") || href.startsWith("https://")) {
    return href;
  }

  const existingLocale = getLocaleFromHref(href);
  if (existingLocale) {
    return href;
  }

  const { pathname, query, hash } = splitHref(href);
  const normalizedPathname = normalizePathname(pathname);

  if (!isPublicLocalePathname(normalizedPathname)) {
    return href;
  }

  const localizedPathname =
    normalizedPathname === "/"
      ? locale === DEFAULT_APP_LOCALE
        ? "/"
        : `/${locale}`
      : `/${locale}${normalizedPathname}`;

  return `${localizedPathname}${query}${hash}`;
};

export const resolveRequestLocale = (
  cookieLocale: string | undefined,
  browserLocale: string | null
): SupportedLocale =>
  resolveSupportedLocale(cookieLocale ?? browserLocale, DEFAULT_APP_LOCALE);
